import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../services/GetTemplate';
import { handlePostRequest } from '../../services/PostTemplate';
import { Paginator } from 'primereact/paginator';
import QRCode from 'qrcode.react';
import moment from 'moment';
import { InputText } from 'primereact/inputtext';

function getEndpointsByRole(role) {
    switch (role) {
        case 'Fitness Officer':
            return {
                getAll: '/api/fitness/batch',
                create: '/api/fitness/batch/create',
                payNow: '/api/fitness/batch/DQR/'
            };
        case 'Data Entry Operator':
            return {
                getAll: '/api/permit/batch',
                create: '/api/permit/batch/create',
                payNow: '/api/permit/batch/DQR/'
            };
        default:
            return {
                getAll: '/api/fitness/batch',
                create: '/api/fitness/batch/create',
                payNow: '/api/fitness/batch/DQR/'
            };
    }
}

const BatchManagement = () => {
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createDialogVisible, setCreateDialogVisible] = useState(false);
    const [qrDialogVisible, setQrDialogVisible] = useState(false);
    const [payNowDialogVisible, setPayNowDialogVisible] = useState(false);
    const [qrValue, setQrValue] = useState('');
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(1);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');


    // Sample QR Code String
    var qrString =
        '0002010102122876003234f12a2b87ec46be9053c4bb15ff7ece0108JSBLPKKA0224PK24JSBL999990343562580952041520530358654035005802PK5917DCOFFICEISLAMABAD6009ISLAMABAD6248011045940202850317DCOFFICEISLAMABAD07099462303918476003233a666ce835eae651004443f3d463d53013634f12a2b-87ec-46be-9053-c4bb15ff7ece63041488';

    const dispatch = useDispatch();
    const toast = useRef(null);

    const roleName = localStorage.getItem('roleName') || 'Data Entry Operator';
    const endpoints = getEndpointsByRole(roleName);

    const fetchBatches = async (pageNumber = 1) => {
        setLoading(true);
        const response = await handleGetRequest(endpoints.getAll);
        if (response?.success) {
            setBatches(response.data || []);
            setTotalRecords(response.data?.length || 0);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchBatches(page);
    }, []);

    const onPageChange = (event) => {
        const newPage = Math.floor(event.first / event.rows) + 1;
        setFirst(event.first);
        setRows(event.rows);
        setPage(newPage);
        fetchBatches(newPage);
    };

    const handleCreateNewBatch = async () => {
        setLoading(true);
        let response = await handleGetRequest(endpoints.create, true);

        if (response?.success) {
            setCreateDialogVisible(false);
            fetchBatches(page);
        }
        setCreateDialogVisible(false)
        setLoading(false);
    };

    const handlePayNow = async () => {
        setLoading(true);
        const payUrl = `${endpoints.payNow}${selectedBatch.id}`;
        const response = await dispatch(handlePostRequest({}, payUrl));
        if (response?.success || qrString) {
            setQrValue(response.data || qrString);
            setQrDialogVisible(true);
        } 
        setPayNowDialogVisible(false);
        setLoading(false);
    };

    const actionBodyTemplate = (rowData) => (
        <Button
            tooltip={rowData?.batchStatus === 'Paid'? 'Batch is already paid' : null}
            disabled={rowData?.batchStatus === 'Paid'}
            label="Pay Now"
            className="px-4 py-2 font-semibold"
            style={{ backgroundColor: '#FF556A', border: 'none', borderRadius: '10px' }}
            onClick={() => {
                if (rowData?.batchStatus === 'UnPaid') {
                     setSelectedBatch(rowData);
                     setPayNowDialogVisible(true);
                } else {
                    return null
                }
            }}
        />
    );

    const template1 = {
        layout: 'CurrentPageReport',
        CurrentPageReport: (options) => (
            <span style={{ color: 'var(--text-color)', userSelect: 'none', fontSize: '12px', fontWeight: '400' }}>
                Showing{' '}
                <strong>
                    {options.first} - {Math.min(options.first + options.rows - 1, options.totalRecords)}
                </strong>{' '}
                from <strong>{options.totalRecords}</strong> records
            </span>
        )
    };

    const template2 = {
        layout: 'PrevPageLink PageLinks NextPageLink',
        PrevPageLink: (options) => <Button icon="pi pi-chevron-left" className="p-button prev-page-link" onClick={options.onClick} disabled={options.disabled} />,
        PageLinks: (options) => {
            const isActive = options.currentPage === options.page;
            return (
                <button
                    type="button"
                    className={`mx-1 px-4 py-2 ${isActive ? 'isActivePage' : 'notActivePage'}`}
                    onClick={options.onClick}
                    style={{
                        width: '40px',
                        height: '40px',
                        fontSize: '14px',
                        fontWeight: '600',
                        borderRadius: '8px',
                        margin: '2px',
                        border: 'none',
                        boxShadow: isActive ? '0px 7px 16px 0px #00000024' : 'none'
                    }}
                >
                    {options.page + 1}
                </button>
            );
        },
        NextPageLink: (options) => <Button icon="pi pi-chevron-right" className="p-button-text prev-page-link" onClick={options.onClick} disabled={options.disabled} />
    };

    return (
        <>
            <Toast ref={toast} />

            <Dialog visible={createDialogVisible} header="Create New Batch" onHide={() => setCreateDialogVisible(false)} style={{ width: '30vw' }}>
                <p>Are you sure you want to create a new payment batch?</p>
                <div className="flex gap-2 justify-content-end">
                    <Button label="No" className="p-button-rounded p-button-text" onClick={() => setCreateDialogVisible(false)} />
                    <Button label="Yes" className="p-button-rounded p-button-success" onClick={handleCreateNewBatch} loading={loading} />
                </div>
            </Dialog>

            <Dialog visible={payNowDialogVisible} header="Confirm Payment" onHide={() => setPayNowDialogVisible(false)} style={{ width: '30vw' }}>
                <p>Are you sure you want to process payment for this batch?</p>
                <div className="flex gap-2 justify-content-end">
                    <Button label="No" className="p-button-rounded p-button-text" onClick={() => setPayNowDialogVisible(false)} />
                    <Button label="Yes" className="p-button-rounded p-button-success" onClick={handlePayNow} loading={loading} />
                </div>
            </Dialog>

            <Dialog
                visible={qrDialogVisible}
                closable={false}
                draggable={false}
                style={{ width: '40vw', textAlign: 'center' }}
                header={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 className="m-2" style={{ textAlign: 'left' }}>
                            Scan QR Code for online payment
                        </h2>
                        <Button icon="pi pi-times" className="p-button-text p-button-rounded p-button-danger" onClick={() => setQrDialogVisible(false)} />
                    </div>
                }
                onHide={() => setQrDialogVisible(false)}
            >
                {qrValue ? (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem' }}>
                            <QRCode value={qrValue?.qrString} size={256} />
                        </div>
                        <p>
                            Use QR Code or use consumer id: <strong>{qrValue?.consumerId}</strong> for payment
                        </p>
                    </>
                ) : (
                    <p>No QR data returned.</p>
                )}
            </Dialog>
            <div className="grid">
            <div className="col-12 flex flex-column">
            <div className="flex justify-content-between items-center">
                <h2 className="text-lg font-semibold">Batch Payments</h2>
                <div className="flex items-center gap-2">
                    {/* <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." className="p-inputtext-sm rounded-input" />
                    </span> */}
                    <Button label="Create New Batch" icon="pi pi-plus" className="px-4 py-2 font-semibold" style={{ backgroundColor: '#FF556A', border: 'none', borderRadius: '10px' }} onClick={() => setCreateDialogVisible(true)} />
                </div>
            </div>
            <div className="card mt-3">
            <div className="mt-3" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1, overflow: 'auto' }}>
                    <DataTable 
                        value={batches} 
                        loading={loading} 
                        responsiveLayout="scroll" 
                        className="datatable-responsive shadow-md rounded-xl"
                        scrollable 
                        scrollHeight="45vh"
                        frozenWidth="20rem"
                        stickyHeader 
                    >
                        <Column field="batchId" header="Batch ID" />
                        <Column field="batchAmount" header="Amount" />
                        <Column
                            field="batchStatus"
                            header="Status"
                            body={(rowData) => (
                                <div
                                    className={`py-1 rounded-full text-sm font-semibold flex justify-content-center items-center ${
                                        rowData.batchStatus === 'Paid' ? 'bg-[#E6F4EA] text-[#3A974C] Active-column' : rowData.batchStatus === 'UnPaid' ? 'bg-[#FFEFE9] text-[#FF8F6B] Inactive-column' : ''
                                    }`}
                                >
                                    {rowData.batchStatus}
                                </div>
                            )}
                        />
                        <Column field="createdOn" header="Created On" body={(rowData) => (rowData.createdOn ? moment(rowData.createdOn).format('DD-MM-YYYY') : 'NA')} />
                        <Column header="Actions" body={actionBodyTemplate} />
                    </DataTable>
                    <div className="flex justify-content-between mb-3 sticky custom-margin-top" style={{ borderTop: '1px solid #efefef' }}>
                        <Paginator template={template1} first={first} rows={rows} totalRecords={totalRecords} onPageChange={onPageChange} currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries" />
                        <Paginator template={template2} first={first} rows={rows} totalRecords={totalRecords} onPageChange={onPageChange} rowsPerPageOptions={[10, 25, 50]} />
                    </div>
                </div>
            </div></div>
            </div>
            </div>
        </>
    );
};

export default BatchManagement;
